import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import type { Profile } from '../lib/data';
import { Portrait } from '../components/Portrait';
import { Chip, TubePill, CircleBtn, Label, VitalRow, FakeMap } from '../components/common';
import { Icon } from '../components/icons';

interface ProfileDetailProps {
  profile: Profile;
  t: Theme;
  dark: boolean;
  onBack: () => void;
  onLike: () => void;
  onPass: () => void;
}

export function ProfileDetail({ profile: p, t, onBack, onLike, onPass }: ProfileDetailProps) {
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', background: t.paper,
    }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 110 }} className="scroll-container">
        {/* Hero photo */}
        <div style={{ position: 'relative' }}>
          <Portrait seed={p.seed} label={p.name} sub={p.neighborhood} style={{ height: 520 }} />

          <button onClick={onBack} style={{
            all: 'unset', cursor: 'pointer',
            position: 'absolute', top: 12, left: 16,
            width: 40, height: 40, borderRadius: 100,
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{Icon.back({ s: 20, c: '#fff' })}</button>

          <button style={{
            all: 'unset', cursor: 'pointer',
            position: 'absolute', top: 12, right: 16,
            width: 40, height: 40, borderRadius: 100,
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{Icon.more({ s: 20, c: '#fff' })}</button>

          {/* Photo dots */}
          <div style={{ position: 'absolute', top: 12, left: 64, right: 64, display: 'flex', gap: 4 }}>
            {Array.from({ length: p.photos }).map((_, i) => (
              <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)' }} />
            ))}
          </div>

          {/* Name slab */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            padding: '60px 24px 22px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1 style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400, fontSize: 44, margin: 0, lineHeight: 1, letterSpacing: -0.5 }}>
                {p.name}
              </h1>
              <span style={{ fontSize: 26, fontFamily: FONTS.sans, fontWeight: 300 }}>{p.age}</span>
              <div style={{ flex: 1 }} />
            </div>
            <div style={{ marginTop: 8, fontFamily: FONTS.sans, fontSize: 14, opacity: 0.92 }}>{p.job}</div>
          </div>
        </div>

        {/* Quick facts */}
        <div style={{ padding: '20px 20px 4px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <TubePill line={p.line} t={t} />
          <Chip t={t}>{Icon.pin({ s: 11, c: t.inkSoft })} {p.neighborhood}</Chip>
          <Chip t={t}>{p.distance}</Chip>
          <Chip t={t}>{p.height}</Chip>
          <Chip t={t}>{p.star}</Chip>
        </div>

        {/* About */}
        <div style={{ padding: '22px 24px 8px' }}>
          <Label t={t}>About</Label>
          <p style={{
            fontFamily: FONTS.display, fontSize: 21, lineHeight: 1.35,
            color: t.ink, margin: '6px 0 0', fontWeight: 400, letterSpacing: -0.2,
          }}>
            {p.prompts[0]?.a}
          </p>
        </div>

        {/* Interests */}
        <div style={{ padding: '18px 24px 6px' }}>
          <Label t={t}>Into</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
            {p.interests.map((x, i) => <Chip key={i} t={t} filled={i === 0}>{x}</Chip>)}
          </div>
        </div>

        {/* Second photo */}
        <div style={{ padding: '20px 20px 4px' }}>
          <Portrait seed={p.seed + 'b'} label={`${p.name} / 02`} style={{ height: 320, borderRadius: 20 }} />
        </div>

        {/* Prompt 2 */}
        {p.prompts[1] && (
          <div style={{ padding: '18px 24px' }}>
            <div style={{ borderRadius: 18, padding: '18px 20px', background: t.card, border: `1px solid ${t.line}` }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: t.inkMute, marginBottom: 6 }}>
                {p.prompts[1].q}
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, lineHeight: 1.3, color: t.ink }}>
                &ldquo;{p.prompts[1].a}&rdquo;
              </div>
            </div>
          </div>
        )}

        {/* Vitals */}
        <div style={{ padding: '6px 24px 12px' }}>
          <Label t={t}>The basics</Label>
          <div style={{ marginTop: 10, borderRadius: 16, background: t.card, border: `1px solid ${t.line}`, overflow: 'hidden' }}>
            <VitalRow t={t} label="Height" value={p.height} />
            <VitalRow t={t} label="Star sign" value={p.star} />
            <VitalRow t={t} label="Smokes" value={p.smokes} />
            <VitalRow t={t} label="Drinks" value={p.drinks} last />
          </div>
        </div>

        {/* Prompt 3 */}
        {p.prompts[2] && (
          <div style={{ padding: '6px 24px 12px' }}>
            <div style={{ borderRadius: 18, padding: '18px 20px', background: t.blush, color: '#1F1A17' }}>
              <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, opacity: 0.65, marginBottom: 6 }}>
                {p.prompts[2].q}
              </div>
              <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, lineHeight: 1.3 }}>
                &ldquo;{p.prompts[2].a}&rdquo;
              </div>
            </div>
          </div>
        )}

        {/* Map */}
        <div style={{ padding: '6px 24px 24px' }}>
          <Label t={t}>Local to</Label>
          <div style={{ marginTop: 10, borderRadius: 16, overflow: 'hidden', position: 'relative', background: t.paperAlt, height: 160, border: `1px solid ${t.line}` }}>
            <FakeMap t={t} neighborhood={p.neighborhood} />
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '0 24px 40px' }}>
          <button style={{
            all: 'unset', cursor: 'pointer',
            fontFamily: FONTS.sans, fontSize: 12, color: t.inkMute, textDecoration: 'underline',
          }}>Report {p.name}</button>
        </div>
      </div>

      {/* Action bar */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: '14px 28px 30px', display: 'flex', gap: 14, justifyContent: 'center',
        background: `linear-gradient(to top, ${t.paper} 60%, transparent)`,
      }}>
        <CircleBtn t={t} size={60} accent={t.terracotta} onClick={onPass}>
          {Icon.x({ s: 26, c: t.terracotta, w: 2.2 })}
        </CircleBtn>
        <CircleBtn t={t} size={50}>
          {Icon.star({ s: 22, c: t.gold, fill: t.gold })}
        </CircleBtn>
        <CircleBtn t={t} size={60} accent={t.sage} onClick={onLike}>
          {Icon.heart({ s: 26, c: t.sage, fill: t.sage, w: 0 })}
        </CircleBtn>
      </div>
    </div>
  );
}
